import { useCallback } from 'react';
import { useConfirmStore, type ConfirmConfig } from '@/stores/useConfirm';

/**
 * Hook for showing confirmation dialogs
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const confirm = useConfirm();
 *   
 *   const handleDelete = async () => {
 *     const result = await confirm({
 *       title: 'Delete Item',
 *       description: 'Are you sure you want to delete this item? This action cannot be undone.',
 *       variant: 'destructive',
 *       confirmText: 'Delete',
 *       cancelText: 'Cancel'
 *     });
 *     
 *     if (result) {
 *       // User clicked "Delete"
 *       deleteItem();
 *     }
 *   };
 * 
 *   return (
 *     <button onClick={handleDelete}>Delete Item</button>
 *   );
 * }
 * ```
 * 
 * @returns A function that shows a confirmation dialog and returns a Promise<boolean>
 */
export function useConfirmHandler() {
  const confirm = useConfirmStore((state) => state.confirm);

  return useCallback(
    (config: ConfirmConfig): Promise<boolean> => {
      return confirm(config);
    },
    [confirm]
  );
}

/**
 * Hook for showing a simple confirmation dialog with common defaults
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const confirmAction = useConfirmAction();
 *   
 *   const handleDelete = async () => {
 *     const result = await confirmAction(
 *       'Delete Item',
 *       'Are you sure you want to delete this item?'
 *     );
 *     
 *     if (result) {
 *       deleteItem();
 *     }
 *   };
 * 
 *   return (
 *     <button onClick={handleDelete}>Delete Item</button>
 *   );
 * }
 * ```
 * 
 * @param title - The title of the confirmation dialog
 * @param description - The description/message of the confirmation dialog
 * @param variant - The variant of the confirm button ('default' | 'destructive')
 * @returns A Promise<boolean> indicating whether the user confirmed (true) or cancelled (false)
 */
export function useConfirmAction() {
  const confirm = useConfirmStore((state) => state.confirm);

  return useCallback(
    (
      title: string,
      description: string,
      variant: 'default' | 'destructive' = 'default'
    ): Promise<boolean> => {
      return confirm({
        title,
        description,
        variant,
        confirmText: variant === 'destructive' ? 'Delete' : 'Confirm',
        cancelText: 'Cancel',
      });
    },
    [confirm]
  );
} 
